<template>
  <v-container v-if="loadingData" fill-height fluid>
    <v-row justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>
  </v-container>

  <v-container v-else-if="APIError" fill-height fluid>
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-alert border="left" colored-border type="error" elevation="2">
          Sorry, something went wrong.
        </v-alert>
      </v-col>
    </v-row>
  </v-container>

  <v-container v-else-if="!packages" fill-height fluid>
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <p class="text-center subtitle">You don't have any packages yet.</p>
      </v-col>
    </v-row>
  </v-container>

  <v-container v-else fluid>
    <v-row>
      <v-col>
				<v-btn color="primary" href="/" outlined class="mr-2">
					<v-icon class="rotate-90">mdi-arrow-left</v-icon>
					Back
				</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" align="center">
        <!-- Single invoice: simple button -->
        <v-btn
          v-if="pendingInvoiceList.length === 1"
          color="error darken-1"
          :href="fileUrlPrefix + pendingInvoiceList[0].invoice_file"
          target="_blank"
          style="font-size: 14px"
          download
        >
          Pending Invoice: Click to view
        </v-btn>

        <!-- Multiple invoices: dropdown -->
        <v-menu v-else-if="pendingInvoiceList.length > 1" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="error darken-1" v-bind="attrs" v-on="on" style="font-size: 14px">
              Pending Invoices
              <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(invoice, index) in pendingInvoiceList"
              :key="index"
              :href="fileUrlPrefix + invoice.invoice_file"
              target="_blank"
              download
            >
              <v-list-item-title style="font-size: 14px">
                Invoice {{ invoice.invoice_number }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="packages"
              :footer-props="{
                'items-per-page-options': [10, 15, 30, -1],
              }"
              :items-per-page="15"
            >
              <template v-slot:[`item.lastStatusUpdate`]="{ item }">
                {{ getLastStatusUpdateDate(item.statusRecords) }}
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                {{ item.invoice ? item.invoice.amount : "" }}
              </template>
              <template v-slot:[`item.shipmentRequest`]="{ item }">
                <v-tooltip top color="primary" v-if="
                  item.statusCode === 'miami' && !item.shipmentRequested && pendingInvoice
                ">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                      
                        color="primary"
                        x-small
                        disabled
                        @click="openRequestShippingDialog(item)"
                      >
                        Click to Ship Packages
                      </v-btn>
                    </span>
                  </template>
                  <span>Unable to proceed until pending invoice has been paid</span>
                </v-tooltip>
                <v-btn
                  v-else-if="item.statusCode === 'delivering_to_miami'"
                  color="primary"
                  x-small
                  disabled
                  >Click to Ship Packages</v-btn
                >
                <v-btn
                  v-else-if="
                    item.statusCode === 'miami' &&
                    !item.shipmentRequested &&
                    !item.invoice
                  "
                  color="warning"
                  x-small
                  outlined
                  @click.stop="createNewInvoice(item)"
                  >Waiting for Invoice</v-btn
                >
                <v-btn
                  v-else-if="
                    item.statusCode === 'miami' && !item.shipmentRequested
                  "
                  color="primary"
                  x-small
                  :loading="item.waitingForUpdate"
                  @click="openRequestShippingDialog(item)"
                  >Click to Ship Packages</v-btn
                >
                <div
                  v-else-if="
                    ['miami', 'port'].includes(item.statusCode) &&
                    item.estimatedArrivalDate
                  "
                >
                  Arriving
                  {{
                    /* isShipmentDatePastDue(item.estimatedArrivalDate)
                      ? "soon"
                      : getFormattedDate(item.estimatedArrivalDate) */
                       getFormattedDate(item.estimatedArrivalDate) 
                  }}
                </div>
                <div v-else>
                  <v-icon color="success"> mdi-check </v-icon>
                </div>
              </template>
              <template v-slot:[`item.invoice`]="{ item }">
                <v-icon
                  v-if="!item.invoice"
                  @click.stop="createNewInvoice(item)"
                >
                  mdi-file-upload-outline
                </v-icon>
                <div v-else>
                  <v-icon @click.stop="editInvoice(item)" color="success">
                    mdi-file-edit-outline
                  </v-icon>
                </div>
              </template>
              <template v-slot:[`item.description`]="{ item }">
                {{ item.invoice ? item.invoice.description : "" }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="800px">
      <InvoiceForm
        :key="dialogKey"
        :selectedPackage="selectedPackage"
        :action="dialogAction"
        @close-dialog="closeDialog"
        @update-data="getData"
      />
    </v-dialog>
    <v-dialog v-model="requestShippingDialog" max-width="600px">
      <RequestShipmentForm
        :key="dialogKey"
        :selectedPackage="selectedPackage"
        :arrivalDate="getNextArrivalDate()"
        @close-dialog="closeRequestShippingDialog"
        @update-data="getData"
				:timezone="timezone"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import { formatISO, parseISO } from "date-fns";
import { DateTime } from "luxon";

import InvoiceForm from "./InvoiceForm";
import RequestShipmentForm from "./RequestShipmentForm";
import GuideDialog from "../../components/GuideDialog";

export default {
  name: "TrackerDetails",

  components: {
    InvoiceForm,
    RequestShipmentForm,
    GuideDialog,
  },

  data: () => ({
    dialog: false,
    dialogAction: "create",
    dialogKey: "",
    requestShippingDialog: false,
    requestShippingDialogKey: "",
    selectedPackage: undefined,
    timezone: "EST",
    loadingData: true,
    APIError: false,
    data: null,
    pendingInvoice: false,
    pendingInvoiceList: [],
    fileUrlPrefix : process.env.VUE_APP_API_ROOT_URL + "media/",
    fileUrl: "",
    headers: [
      {
        text: "Consignee",
        value: "magayaConsignee",
        sortable: true,
      },
      {
        text: "Shipper",
        value: "magayaShipper",
        sortable: true,
      },
      {
        text: "Pieces",
        value: "magayaPieces",
        sortable: true,
      },
      {
        text: "Tracking number",
        value: "magayaTrackingNumber",
        sortable: true,
      },
      {
        text: "Date Received",
        value: "magayaDate",
        sortable: true,
      },
      {
        text: "Status",
        value: "status",
        sortable: true,
      },
      {
        text: "Upload Invoice",
        value: "invoice",
        sortable: true,
      },
      {
        text: "Amount",
        value: "amount",
        sortable: false,
      },
      {
        text: "Description",
        value: "description",
        sortable: false,
      },
      {
        text: "Request Shipping",
        value: "shipmentRequest",
        sortable: true,
      },
    ],
  }),

  mounted() {
    this.getData();
  },

  computed: {
    packages: function () {
      return this.$store.state.packages;
    },
    currentDateTime: function () {
      return DateTime.now().setZone(this.timezone).toISODate();
    },
  },

  methods: {
    getData: function () {
      const accessCode = this.$route.params.accessCode;
      this.loadingData = true;
      this.APIError = false;
      console.log(process.env.VUE_APP_API_ROOT_URL)
      axios
        .get(`${process.env.VUE_APP_API_ROOT_URL}packages/${accessCode}/`) 
        .then((response) => {
          this.$store.commit("updatePackages", response.data);
          this.loadingData = false;
        })
        .catch((e) => {
          console.log("Error fetching packages:", e);
          this.APIError = true;
          this.loadingData = false;
        });
      axios
        .get(`${process.env.VUE_APP_API_ROOT_URL}pending-invoices/${accessCode}/`)
        .then((response) => {
          if(response.data !== "") {
            this.pendingInvoiceList = response.data;
            if(response.data.length > 0){
            this.pendingInvoice = true;
          }
          }
        })
          .catch((e) => {
            console.log("Error fetching invoices:", e);
            this.APIError = true;
          });
      axios
        .get(`${process.env.VUE_APP_API_ROOT_URL}api/settings`)
        .then((response) => {
          this.timezone = response.data.timezone;
        })
        .catch((error) => {
          console.error("Error fetching settings:", error);
          this.APIError = true;
        });
    },
    requestShipment: function () {
      this.getNextArrivalDate();
    },
    getLastStatusUpdateDate: function (statusRecords) {
      const lastStatusUpdate = statusRecords.slice(-1)[0];
      if (lastStatusUpdate) {
        const date = parseISO(lastStatusUpdate.date);
        return formatISO(date, { representation: "date" });
      }
      return "-";
    },
    createNewInvoice: function (selectedPackage) {
      this.selectedPackage = selectedPackage;
      this.dialogAction = "create";
      this.openDialog();
    },
    editInvoice: function (selectedPackage) {
      this.selectedPackage = selectedPackage;
      this.dialogAction = "edit";
      this.openDialog();
    },
    openDialog: function () {
      if (this.selectedPackage) {
        this.dialogKey = `selected ${this.selectedPackage.id}`;
      } else {
        this.dialogKey = `new invoice`;
      }
      this.dialog = true;
    },
    closeDialog: function () {
      this.dialogKey = "";
      this.dialog = false;
    },
    openRequestShippingDialog: function (selectedPackage) {
      this.selectedPackage = selectedPackage;
      this.requestShippingDialogKey = `requesting shipment ${selectedPackage.id}`;
      this.requestShippingDialog = true;
    },
    closeRequestShippingDialog: function () {
      this.selectedPackage = undefined;
      this.requestShippingDialogKey = "";
      this.requestShippingDialog = false;
    },
    getNextArrivalDate: function () {
      const thursday = 4;
      const numberOfDaysToArrive = 11;
      let shipmentDay = DateTime.now().setZone(this.timezone);
      if (
        shipmentDay.weekday > thursday ||
        (shipmentDay.weekday == thursday && shipmentDay.hour >= 17)
      ) {
        shipmentDay = shipmentDay.plus({ week: 1 });
      }
      shipmentDay = shipmentDay.set({ weekday: thursday });
      let arrivalDate = shipmentDay.plus({ days: numberOfDaysToArrive });
      return arrivalDate;
    },
    isShipmentDatePastDue: function (shipmentDate) {
      const now = DateTime.now().setZone(this.timezone);
      shipmentDate = DateTime.fromISO(shipmentDate);
      return now.startOf("day") > shipmentDate.startOf("day");
    },
    getFormattedDate: function (ISOString) {
      return DateTime.fromISO(ISOString).toFormat("dd-MMM-yyyy");
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-disabled-btn {
  pointer-events: none;
}
</style>
